"use client";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Eye } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { formatCurrency, formatDate } from "@/lib/utils";
import { getBookingTypePresentation } from "@/lib/bookingTypePresentation";
import { Appointment, Customer, Vehicle, Staff } from "@/types";
import {
  getStatusBadgeClasses,
  getStatusLabel,
} from "@/lib/appointmentStatus";

interface UpcomingAppointmentsProps {
  appointments: Appointment[];
  customers: Customer[];
  vehicles: Vehicle[];
  staff: Staff[];
}

function getStatusBadge(status: string) {
  return (
    <Badge variant="outline" className={getStatusBadgeClasses(status)}>
      {getStatusLabel(status)}
    </Badge>
  );
}

export function UpcomingAppointments({
  appointments,
  customers,
  vehicles,
  staff,
}: UpcomingAppointmentsProps) {
  const navigate = useNavigate();

  return (
    <Card className="border-0 shadow-sm">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-lg font-semibold">
              Todays Appointments
            </CardTitle>
            <p className="text-sm text-muted-foreground mt-1">
              Today's scheduled services for your customers
            </p>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/appointments")}
          >
            <Eye className="mr-2 h-4 w-4" />
            View All
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow className="border-b hover:bg-transparent">
              <TableHead className="text-xs font-medium">Date</TableHead>
              <TableHead className="text-xs font-medium">Customer</TableHead>
              <TableHead className="text-xs font-medium">Vehicle</TableHead>
              <TableHead className="text-xs font-medium">Type</TableHead>
              <TableHead className="text-xs font-medium">Booking Type</TableHead>
              <TableHead className="text-xs font-medium">
                Assigned Staff
              </TableHead>
              <TableHead className="text-xs font-medium">Status</TableHead>
              <TableHead className="text-right text-xs font-medium">
                Amount
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {appointments.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={8}
                  className="text-center text-muted-foreground py-8"
                >
                  No appointments for today
                </TableCell>
              </TableRow>
            ) : (
              appointments.map((appointment) => {
                const customer = customers.find(
                  (c) => c.id === appointment.customerId
                );
                const matchedVehicles = (appointment.vehicleIds ?? [])
                  .map((vId) => vehicles.find((v) => v.id === vId))
                  .filter(Boolean) as Vehicle[];

                const vehicleLabel =
                  matchedVehicles.length > 0
                    ? matchedVehicles
                        .map((v) =>
                          (v.make || v.model)
                            ? `${v.make ?? ""} ${v.model ?? ""}`.trim()
                            : v.plateNumber ?? "Unknown"
                        )
                        .join(", ")
                    : null;

                const staffMember = staff.find(
                  (s) => s.id === appointment.staffId
                );

                const staffLabel = (() => {
                  if (!appointment.staffId) return "Unassigned";
                  if (appointment.isStaffDeleted) {
                    return `Deleted staff (${appointment.staffName || "Unknown"})`;
                  }
                  return staffMember?.name || appointment.staffName || "Unassigned";
                })();

                const bookingTypePresentation = getBookingTypePresentation(
                  appointment.bookingType
                );
                const finalAmount =
                  appointment.amount === null
                    ? null
                    : Math.max(
                        0,
                        appointment.amount -
                          (appointment.discount ?? 0) -
                          (appointment.platinumDiscount ?? 0),
                      );

                return (
                  <TableRow key={appointment.id} className="border-b">
                    <TableCell>
                      <span className="text-sm font-medium">
                        {formatDate(appointment.date)}
                      </span>
                    </TableCell>
                    <TableCell className="text-sm">
                      {customer?.name || "N/A"}
                    </TableCell>
                    <TableCell className="text-sm">
                      {vehicleLabel || "N/A"}
                    </TableCell>
                    <TableCell className="text-sm">
                      {appointment.type.replace(/_/g, " ")}
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant="outline"
                        className={bookingTypePresentation.badgeClassName}
                      >
                        {bookingTypePresentation.label}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm">
                      {staffLabel}
                    </TableCell>
                    <TableCell>{getStatusBadge(appointment.status)}</TableCell>
                    <TableCell className="text-right text-sm font-medium">
                      {finalAmount !== null
                        ? formatCurrency(finalAmount)
                        : "N/A"}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}
